import { devLog } from '../../utils/devLog';

import React, { useState, useEffect } from 'react';
import { useAppContext } from '../../hooks/useAppContext';
import { getRoleAnalyses } from '../../utils/api';

const RecentActivity = () => {
  const { jobs } = useAppContext();
  const [recentActivities, setRecentActivities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchActivities = async () => {
      setIsLoading(true);
      const activitiesList = [];

      try {
        // Fetch candidates from all jobs
        const analysesPromises = jobs.map(job => 
          job.roleId ? getRoleAnalyses(job.roleId).then(result => ({ job, candidates: result.data || [] })) : Promise.resolve({ job, candidates: [] })
        );
        
        const results = await Promise.all(analysesPromises);

        // Add candidate activities
        results.forEach(({ job, candidates }) => {
          candidates.slice(0, 2).forEach(candidate => {
            // Add CV upload activity
            activitiesList.push({
              icon: '📄',
              title: 'New CV Uploaded',
              description: `${candidate.name || 'Candidate'} applied for ${job.title}`,
              color: '#667eea',
              timestamp: new Date() // Using current time as we don't have upload timestamp from API
            });

            // Add screening complete activity
            if (candidate.status === 'Completed' || candidate.status === 'interview_scheduled') {
              activitiesList.push({
                icon: '🤖',
                title: 'CV Analysis Complete',
                description: `AI screening completed for ${candidate.name || 'Candidate'}`,
                color: '#10b981',
                timestamp: new Date()
              });
            }
          });
        });
      } catch (error) {
        devLog.error('Error fetching activities:', error);
      }

      // Add job creation activities
      const sortedJobs = [...jobs].sort((a, b) => {
        const dateA = new Date(a.createdAt || 0);
        const dateB = new Date(b.createdAt || 0);
        return dateB - dateA;
      });

      sortedJobs.slice(0, 2).forEach(job => {
        const createdDate = new Date(job.createdAt || Date.now());
        const timeAgo = getTimeAgo(createdDate);
        
        activitiesList.push({
          icon: '📋',
          title: 'New Job Created',
          description: `${job.title} position opened ${timeAgo}`,
          color: '#764ba2',
          timestamp: createdDate
        });
      });

      // Sort all activities by timestamp (most recent first)
      activitiesList.sort((a, b) => b.timestamp - a.timestamp);

      // Take only the 5 most recent activities
      setRecentActivities(activitiesList.slice(0, 5));
      setIsLoading(false);
    };

    fetchActivities();
  }, [jobs]);

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px', color: '#718096' }}>
        <div style={{ fontSize: '2rem', marginBottom: '10px', opacity: 0.5 }}>🔄</div>
        <p>Loading recent activities...</p>
      </div>
    );
  }

  if (recentActivities.length === 0) {
    return (
      <div className="empty-state">
        <div className="empty-icon">📭</div>
        <h3>No Recent Activity</h3>
        <p>Start by creating jobs and uploading CVs</p>
      </div>
    );
  }

  return (
    <div>
      {recentActivities.map((activity, index) => (
        <div 
          key={index}
          style={{
            background: '#f8fafc',
            padding: '15px',
            borderRadius: '10px',
            marginBottom: '10px',
            borderLeft: `4px solid ${activity.color}`,
            transition: 'all 0.3s ease'
          }}
        >
          <div style={{ fontWeight: 600, color: '#2d3748', marginBottom: '5px' }}>
            {activity.icon} {activity.title}
          </div>
          <div style={{ fontSize: '0.9rem', color: '#718096' }}>
            {activity.description}
          </div>
        </div>
      ))}
    </div>
  );
};

// Helper function to get time ago string
const getTimeAgo = (date) => {
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 30) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  return date.toLocaleDateString();
};

export default RecentActivity;
